import React, { useState, useEffect } from "react";
import { database } from "../../firebaseConfig";
import { ref, onValue, get } from "firebase/database";
import { getOfferText, formatDateByLanguage, formatPriceByLanguage, getPaymentType, PAYMENT_TYPES } from "./OfferLanguage";
import "./OfferPreviewPage/OfferPreviewPage.css";

/**
 * OfferPreviewPage Komponente für die Anzeige eines Angebots
 * Diese Komponente kann entweder direkte Angebotsdaten über offerData erhalten
 * oder sie kann Daten basierend auf userId und offerId aus der Datenbank laden
 */
const OfferPreviewPage = ({ userId = null, offerId = null, offerData = null, services = [], onlyPreview = false }) => {
  const [offer, setOffer] = useState(null);
  const [servicesList, setServicesList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [companySettings, setCompanySettings] = useState({
    defaultCompanyName: "BonusClub.io Team"
  });

  // Lädt Angebots- und Service-Daten aus der Datenbank
  useEffect(() => {
    const loadData = async () => {
      // Load company settings if userId is available
      if (userId) {
        try {
          const settingsRef = ref(database, `/World/${userId}/team/settings`);
          const settingsSnapshot = await get(settingsRef);
          
          if (settingsSnapshot.exists()) {
            const settings = settingsSnapshot.val();
            setCompanySettings({
              defaultCompanyName: settings.defaultCompanyName || "BonusClub.io Team"
            });
          }
        } catch (error) {
          console.error("Error loading company settings:", error);
        }
      }
    };

    loadData();

    // Wenn direkte offerData übergeben wurden, diese verwenden
    if (offerData) {
      setOffer(offerData);
      setServicesList(services);
      setIsLoading(false);
      return;
    }
    
    // Ansonsten Daten aus der Datenbank laden, falls userId und offerId vorhanden sind
    if (userId && offerId) {
      setIsLoading(true);
      
      // Angebot laden
      const offerRef = ref(database, `/World/${userId}/offers/${offerId}`);
      onValue(offerRef, (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.val();
          setOffer({ id: offerId, ...data });
          
          // Services für dieses Angebot laden
          const servicesRef = ref(database, `/World/${userId}/services`);
          onValue(servicesRef, (servicesSnapshot) => {
            if (servicesSnapshot.exists()) {
              const servicesData = servicesSnapshot.val();
              const servicesArray = Object.entries(servicesData)
                .map(([id, data]) => ({ id, ...data }))
                .filter(service => data.services?.includes(service.id));
              
              setServicesList(servicesArray);
            }
            setIsLoading(false);
          });
        } else {
          setError("Offer not found");
          setIsLoading(false);
        }
      }, (error) => {
        setError("Error loading offer: " + error.message);
        setIsLoading(false);
      });
    } else if (!offerData) {
      setError("No offer data or IDs provided");
      setIsLoading(false);
    }
  }, [userId, offerId, offerData, services]);

  // Get current language from offer data
  const currentLanguage = offer?.language || offerData?.language || 'en';

  // Hilfsfunktion zum Formatieren von Preisen mit Sprache
  const formatPrice = (price) => {
    return formatPriceByLanguage(price, currentLanguage);
  };

  // Formatiert ein Datum basierend auf der Sprache
  const formatDate = (timestamp) => {
    return formatDateByLanguage(timestamp, currentLanguage);
  };

  // Berechnet den Rabatt in Prozent
  const calculateDiscount = (original, discounted) => {
    if (!original || original === 0) return 0;
    return Math.round((1 - discounted / original) * 100);
  };

  // Hilfsfunktion zum Entfernen von HTML-Tags aus Texten (für Titel, wo wir nur den Text benötigen)
  const removeHtmlTags = (htmlString) => {
    if (!htmlString) return "";
    
    // Erstelle ein temporäres div-Element
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlString;
    
    // Extrahiere den Text
    return tempDiv.textContent || tempDiv.innerText || '';
  };

  // Aktualisierte Funktion für die Formatierung von Textinhalten
  const formatTextContent = (text) => {
    if (!text) return "";
    
    // Verwende DOMPurify für die Bereinigung des HTML
    // Anstatt alle Tags zu entfernen, behalten wir die sicheren Formatierungstags bei
    // aber entfernen potenziell gefährliche Tags wie <script>
    const sanitizedText = text
      .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '') // Entferne script-Tags
      .replace(/<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gi, '') // Entferne iframe-Tags
      .replace(/<style\b[^<]*(?:(?!<\/style>)<[^<]*)*<\/style>/gi, ''); // Entferne style-Tags
    
    // Wir entfernen keine Formatierungstags wie <b>, <i>, <u>, <ul>, <li> etc. mehr!
    
    // Zeilenumbrüche in leeren p-Tags hinzufügen (falls der Quill-Editor diese nicht hinzufügt)
    const withLineBreaks = sanitizedText.replace(/<p><\/p>/g, '<p><br></p>');
    
    return withLineBreaks;
  };

  // Setze die CSS-Variablen für die Logo-Höhe und mobile Labels
  useEffect(() => {
    const root = document.documentElement;
    const logoHeight = offerData.logoMaxHeight || 80; // Fallback auf 80px
    const mobileLogoHeight = Math.min(40, logoHeight / 2); // Mobile-Höhe ist die Hälfte, maximal 40px
    
    root.style.setProperty('--customer-logo-max-height', `${logoHeight}px`);
    root.style.setProperty('--customer-logo-max-height-mobile', `${mobileLogoHeight}px`);
    
    // Setze mobile Labels basierend auf der Sprache
    if (currentLanguage === 'de') {
      root.style.setProperty('--mobile-original-price-label', '"Preis:"');
      root.style.setProperty('--mobile-discount-label', '"%"');
      root.style.setProperty('--mobile-price-label', '"Endpreis:"');
    } else {
      root.style.setProperty('--mobile-original-price-label', '"Original price:"');
      root.style.setProperty('--mobile-discount-label', '"%"');
      root.style.setProperty('--mobile-price-label', '"Price:"');
    }

    return () => {
      // Cleanup beim Unmount
      root.style.removeProperty('--customer-logo-max-height');
      root.style.removeProperty('--customer-logo-max-height-mobile');
      root.style.removeProperty('--mobile-original-price-label');
      root.style.removeProperty('--mobile-discount-label');
      root.style.removeProperty('--mobile-price-label');
    };
  }, [offerData.logoMaxHeight, currentLanguage]);

  // Ladezustand anzeigen
  if (isLoading) {
    return (
      <div className="offer-preview-page__loading">
        <div className="offer-preview-page__spinner"></div>
        <p>{getOfferText(currentLanguage, 'loadingText')}</p>
      </div>
    );
  }

  // Fehler anzeigen
  if (error) {
    return (
      <div className="offer-preview-page__error">
        <div className="offer-preview-page__error-icon">⚠️</div>
        <p>{error}</p>
      </div>
    );
  }

  // Keine Daten verfügbar
  if (!offer) {
    return (
      <div className="offer-preview-page__empty">
        <div className="offer-preview-page__empty-icon">📝</div>
        <p>{getOfferText(currentLanguage, 'noOfferAvailable')}</p>
      </div>
    );
  }

  // Angebot anzeigen
  return (
    <div className={`offer-preview-page ${onlyPreview ? 'offer-preview-page--only-preview' : ''}`}>
      {offer.documentType !== 'invoice' && (
        <div className="offer-preview-page__privacy-notice">
          <p>{getOfferText(currentLanguage, 'privacyNotice')}</p>
        </div>
      )}
      
      <div className="offer-preview-page__header">
        {/* Logo oben */}
        <div className="offer-preview-page__header-logo">
          <img 
            src="/img/BonusClub_Logo.webp" 
            alt="BonusClub Logo" 
            className="offer-preview-page__bonusclub-image" 
          />
        </div>

        {/* Absender und Empfänger Spalten */}
        <div className="offer-preview-page__header-addresses">
          {/* Linke Spalte: Empfänger (Kunde) */}
          <div className="offer-preview-page__recipient-container">
            <div className="offer-preview-page__address-label">
              {currentLanguage === 'de' ? 'Empfänger' : 'To'}
            </div>
            {offer.customerName && (
              <>
                <div className="offer-preview-page__address-company">{offer.customerName}</div>
                {offer.customerAddress && (
                  <div style={{ whiteSpace: 'pre-line' }}>{offer.customerAddress}</div>
                )}
              </>
            )}
          </div>

          {/* Rechte Spalte: Absender (Team Member) */}
          <div className="offer-preview-page__sender-container">
            <div className="offer-preview-page__address-label">
              {currentLanguage === 'de' ? 'Absender' : 'From'}
            </div>
            {offerData.teamMember ? (
              <>
                <div className="offer-preview-page__address-company">{offerData.teamMember.companyName}</div>
                {offerData.teamMember.name && offerData.showSenderName !== false && (
                  <div style={{ fontWeight: '500', marginBottom: '4px' }}>{offerData.teamMember.name}</div>
                )}
                {offerData.teamMember.billingStreet && (
                  <>
                    <div>{offerData.teamMember.billingStreet}</div>
                    <div>{offerData.teamMember.billingZip} {offerData.teamMember.billingCity}</div>
                    {offerData.teamMember.billingCountry && <div>{offerData.teamMember.billingCountry}</div>}
                  </>
                )}
                {offerData.teamMember.vatId && (
                  <div style={{ marginTop: '8px', fontSize: '0.85rem' }}>
                    {currentLanguage === 'de' ? 'USt-IdNr.' : 'VAT ID'}: {offerData.teamMember.vatId}
                  </div>
                )}
              </>
            ) : (
              <div className="offer-preview-page__address-company">{companySettings.defaultCompanyName}</div>
            )}
          </div>
        </div>

        {/* Meta-Informationen (Rechnungsnummer, Datum, etc.) */}
        <div className="offer-preview-page__meta-container">
          {offer.documentType === 'invoice' && offer.invoiceDate && (
            <div className="offer-preview-page__invoice-date">
              <span className="offer-preview-page__meta-label">
                {currentLanguage === 'de' ? 'Rechnungsdatum:' : 'Invoice Date:'}
              </span>
              <span className="offer-preview-page__invoice-date-value">{formatDate(offer.invoiceDate)}</span>
            </div>
          )}

          {offer.documentType === 'invoice' && offer.invoiceNumber && (
            <div className="offer-preview-page__invoice-number">
              <span className="offer-preview-page__meta-label">
                {currentLanguage === 'de' ? 'Rechnungsnummer:' : 'Invoice Number:'}
              </span>
              <span className="offer-preview-page__invoice-number-value">
                {offer.invoiceNumber}
              </span>
            </div>
          )}

          {offer.documentType !== 'invoice' && offer.showOfferNumber && offer.offerNumber && (
            <div className="offer-preview-page__offer-number">
              <span className="offer-preview-page__meta-label">
                {getOfferText(currentLanguage, 'offerNumberLabel')}
              </span>
              <span className="offer-preview-page__offer-number-value">
                {offer.offerNumber}
              </span>
            </div>
          )}

          {offer.documentType !== 'invoice' && offer.createdAt && (
            <div className="offer-preview-page__date">
              <span className="offer-preview-page__meta-label">{getOfferText(currentLanguage, 'createdOnLabel')}</span>
              <span className="offer-preview-page__date-value">{formatDate(offer.createdAt)}</span>
            </div>
          )}
          
          {offer.offerValidTill && (
            <div className="offer-preview-page__valid-till">
              <span className="offer-preview-page__meta-label">
                {getOfferText(currentLanguage, offer.documentType === 'invoice' ? 'paymentDueLabel' : 'validUntilLabel')}
              </span>
              <span className="offer-preview-page__valid-till-value">{formatDate(offer.offerValidTill)}</span>
            </div>
          )}
          
          {offer.estimatedTimespan && (
            <div className="offer-preview-page__timespan">
              <span className="offer-preview-page__meta-label">{getOfferText(currentLanguage, 'deliveryTimeLabel')}</span>
              <span className="offer-preview-page__timespan-value">{offer.estimatedTimespan}</span>
            </div>
          )}

          {offer.reference && (
            <div className="offer-preview-page__reference">
              <span className="offer-preview-page__meta-label">{getOfferText(currentLanguage, 'referenceLabel')}</span>
              <span className="offer-preview-page__reference-value">{offer.reference}</span>
            </div>
          )}
        </div>
        

        
        <div className="offer-preview-page__title-container">
          <div className="offer-preview-page__document-type-badge">
            {offer.documentType === 'invoice' 
              ? (currentLanguage === 'de' ? 'RECHNUNG' : 'INVOICE')
              : (currentLanguage === 'de' ? 'ANGEBOT' : 'OFFER')
            }
          </div>
          <h1 className="offer-preview-page__title">{offer.title}</h1>
        </div>
        {offer.description && (
        <div className="offer-preview-page__description">
          <p dangerouslySetInnerHTML={{ __html: formatTextContent(offer.description) }}></p>
        </div>
      )}
     
      </div>

   

      <div className="offer-preview-page__services">
        <h2 className="offer-preview-page__services-heading">
          {getOfferText(currentLanguage, 'includedServicesHeading')}
        </h2>

        {servicesList.length === 0 ? (
          <div className="offer-preview-page__no-services">
            <p>{getOfferText(currentLanguage, 'noServicesSelected')}</p>
          </div>
        ) : (
          <div className="offer-preview-page__services-grid">
            {/* Header der Services-Auflistung */}
            <div className="offer-preview-page__services-header">
              <div className="offer-preview-page__grid-cell offer-preview-page__grid-cell--service">
                {getOfferText(currentLanguage, 'serviceColumnHeader')}
              </div>
              <div className="offer-preview-page__grid-cell offer-preview-page__grid-cell--price">
                {getOfferText(currentLanguage, 'priceColumnHeader')}
              </div>
              <div className="offer-preview-page__grid-cell offer-preview-page__grid-cell--discount">
                {getOfferText(currentLanguage, 'discountColumnHeader')}
              </div>
              <div className="offer-preview-page__grid-cell offer-preview-page__grid-cell--final">
                {getOfferText(currentLanguage, 'finalColumnHeader')}
              </div>
            </div>
            
            {/* Services Einträge */}
            {servicesList.map(service => {
              const originalPrice = service.price || 0;
              const discount = service.discountPercent || 0;
              const discountedPrice = originalPrice * (1 - discount / 100);
              
              return (
                <div key={service.id} className="offer-preview-page__services-row">
                  <div className="offer-preview-page__grid-cell offer-preview-page__grid-cell--service">
                    <div className="offer-preview-page__service-info">
                      <div className="offer-preview-page__service-name">
                        {service.title ? removeHtmlTags(service.title) : "Unnamed Service"}
                        {(() => {
                          const pt = getPaymentType(service);
                          if (pt === PAYMENT_TYPES.RECURRING) {
                            return (
                              <span className="offer-preview-page__service-recurring-badge">
                                {getOfferText(currentLanguage, 'monthlyBadge')}
                              </span>
                            );
                          }
                          if (pt === PAYMENT_TYPES.PER_APPOINTMENT) {
                            return (
                              <span className="offer-preview-page__service-recurring-badge offer-preview-page__service-per-appointment-badge">
                                {getOfferText(currentLanguage, 'perAppointmentBadge')}
                              </span>
                            );
                          }
                          return null;
                        })()}
                      </div>
                      {service.description && (
                        <div className="offer-preview-page__service-description">
                          <div dangerouslySetInnerHTML={{ __html: formatTextContent(service.description) }}></div>
                        </div>
                      )}
                      
                      {/* Microservices anzeigen, falls vorhanden */}
                      {service.microservices && service.microservices.length > 0 && (
                        <div className="offer-preview-page__microservices">
                          <ul className="offer-preview-page__microservices-list">
                            {service.microservices.map((microservice, index) => (
                              <li key={index} className="offer-preview-page__microservice-item">
                                {removeHtmlTags(microservice.title || microservice.name || "Microservice")}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="offer-preview-page__grid-cell offer-preview-page__grid-cell--price">
                    {formatPrice(originalPrice)}
                  </div>
                  <div className="offer-preview-page__grid-cell offer-preview-page__grid-cell--discount">
                    {discount > 0 ? `${discount}%` : "-"}
                  </div>
                  <div className="offer-preview-page__grid-cell offer-preview-page__grid-cell--final">
                    {formatPrice(discountedPrice)}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="offer-preview-page__summary">
        <div className="offer-preview-page__summary-row offer-preview-page__summary-subtotal">
          <span>{getOfferText(currentLanguage, 'totalValueOfServices')}</span>
          <span>{formatPrice(offer.originalTotal || 0)}</span>
        </div>

        {(offer.originalTotal !== offer.servicesDiscountedTotal) && (
          <div className="offer-preview-page__summary-row offer-preview-page__summary-savings">
            <span>{getOfferText(currentLanguage, 'serviceDiscounts')}</span>
            <span>
              {formatPrice((offer.originalTotal || 0) - (offer.servicesDiscountedTotal || 0))} 
              ({calculateDiscount(offer.originalTotal, offer.servicesDiscountedTotal)}%)
            </span>
          </div>
        )}

        {offer.globalDiscount > 0 && (
          <div className="offer-preview-page__summary-row offer-preview-page__summary-global-discount">
            <span>{getOfferText(currentLanguage, 'globalDiscount')}</span>
            <span>
              {formatPrice((offer.servicesDiscountedTotal || 0) - (offer.discountedTotal || 0))} 
              ({offer.globalDiscount}%)
            </span>
          </div>
        )}

        <div className="offer-preview-page__summary-row offer-preview-page__summary-subtotal">
          <span>{getOfferText(currentLanguage, 'subtotal')}</span>
          <span>{formatPrice(offer.discountedTotal || 0)}</span>
        </div>

        {offer.includeTax && offer.taxAmount > 0 && !offer.reverseCharge && (
          <div className="offer-preview-page__summary-row offer-preview-page__summary-tax">
            <span>{getOfferText(currentLanguage, 'taxes')} ({offer.taxRate}%)</span>
            <span>{formatPrice(offer.taxAmount || 0)}</span>
          </div>
        )}

        <div className="offer-preview-page__summary-row offer-preview-page__summary-total">
          <span>{getOfferText(currentLanguage, 'offerPrice')}</span>
          <div className="offer-preview-page__price-container">
            <span className="offer-preview-page__total-price">{formatPrice(offer.finalTotal || offer.discountedTotal || 0)}</span>
            
            <div className="offer-preview-page__price-breakdown">
              {(() => {
                // Helper: total (incl. global discount + tax) for services matching a payment type
                const totalForType = (type) => {
                  const subtotal = servicesList
                    .filter(s => getPaymentType(s) === type)
                    .reduce(
                      (sum, s) => sum + ((s.price || 0) - ((s.price || 0) * (s.discountPercent || 0) / 100)),
                      0
                    ) * (1 - (offer.globalDiscount || 0) / 100);
                  const tax = (offer.includeTax && !offer.reverseCharge) ? subtotal * ((offer.taxRate || 0) / 100) : 0;
                  return subtotal + tax;
                };

                const hasOneTime = servicesList.some(s => getPaymentType(s) === PAYMENT_TYPES.ONE_TIME);
                const hasPerAppointment = servicesList.some(s => getPaymentType(s) === PAYMENT_TYPES.PER_APPOINTMENT);
                const hasRecurring = servicesList.some(s => getPaymentType(s) === PAYMENT_TYPES.RECURRING);
                const hasMixed = [hasOneTime, hasPerAppointment, hasRecurring].filter(Boolean).length > 1;

                return (
                  <>
                    {hasOneTime && (
                      <div className="offer-preview-page__price-breakdown-item">
                        <span className="offer-preview-page__price-breakdown-label">
                          {getOfferText(currentLanguage, 'oneTimePayment')}
                        </span>
                        <span className="offer-preview-page__price-breakdown-value">
                          {formatPrice(
                            hasMixed
                              ? totalForType(PAYMENT_TYPES.ONE_TIME)
                              : (offer.finalTotal || offer.discountedTotal || 0)
                          )}
                        </span>
                      </div>
                    )}
                    {hasPerAppointment && (
                      <div className="offer-preview-page__price-breakdown-item">
                        <span className="offer-preview-page__price-breakdown-label">
                          {getOfferText(currentLanguage, 'perAppointmentPayment')}
                        </span>
                        <span className="offer-preview-page__price-breakdown-value">
                          {formatPrice(totalForType(PAYMENT_TYPES.PER_APPOINTMENT))}
                          <span className="offer-preview-page__price-breakdown-suffix">
                            {' '}{getOfferText(currentLanguage, 'pricePerAppointmentSuffix')}
                          </span>
                        </span>
                      </div>
                    )}
                    {hasRecurring && (
                      <div className="offer-preview-page__price-breakdown-item">
                        <span className="offer-preview-page__price-breakdown-label">
                          {getOfferText(currentLanguage, 'monthlyPayment')}
                        </span>
                        <span className="offer-preview-page__price-breakdown-value">
                          {formatPrice(totalForType(PAYMENT_TYPES.RECURRING))}
                        </span>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      </div>

      {/* Reverse Charge Info - positioned after the summary section */}
      {offer.reverseCharge && (
        <div className="offer-preview-page__reverse-charge-info">
          <p><strong>{getOfferText(currentLanguage, 'reverseCharge')}</strong></p>
          <p>{getOfferText(currentLanguage, 'reverseChargeInfo')}</p>
        </div>
      )}

      {/* Add payment button after the reverse charge info */}
      {offerData.showPaymentButton && offerData.paymentButtonLink && (
        <div className="offer-preview__payment-section">
          <a
            href={offerData.paymentButtonLink}
            target="_blank"
            rel="noopener noreferrer"
            className="offer-maker__payment-button"
            style={{ textDecoration: 'none' }}
          >
            {offerData.paymentButtonText || getOfferText(currentLanguage, 'defaultPaymentButtonText')}
          </a>
        </div>
      )}

      {offer.notes && (
          <div className="offer-preview-page__notes">
            <p dangerouslySetInnerHTML={{ __html: formatTextContent(offer.notes) }}></p>
          </div>
        )}

      {offer.terms && (
        <div className="offer-preview-page__terms">
          <h3 className="offer-preview-page__terms-heading">
            <span className="offer-preview-page__terms-icon">§</span>
            {getOfferText(currentLanguage, 'termsHeading')}
          </h3>
          <div
            className="offer-preview-page__terms-content"
            dangerouslySetInnerHTML={{ __html: formatTextContent(offer.terms) }}
          />
        </div>
      )}

      {offer.showSignatureSection && (() => {
        const customerSignerName =
          (offer.signatureCustomerName && offer.signatureCustomerName.trim()) ||
          offer.customerName || '';
        const customerSignerRole = offer.signatureCustomerRole || '';
        const providerName = offerData.teamMember?.name || '';
        const providerRole = offerData.teamMember?.role || '';
        const providerCompany = offerData.teamMember?.companyName || companySettings.defaultCompanyName;
        const providerSignature = offerData.teamMember?.signature;
        const providerSignatureMaxWidth = offerData.teamMember?.signatureMaxWidth || 150;
        const placeDate = offer.signaturePlace || '';

        return (
          <div className="offer-preview-page__signature-section">
            <h3 className="offer-preview-page__signature-heading">
              {getOfferText(currentLanguage, 'signatureHeading')}
            </h3>
            <p className="offer-preview-page__signature-intro">
              {getOfferText(currentLanguage, 'signatureIntro')}
            </p>

            <div className="offer-preview-page__signature-grid">
              {/* Customer column */}
              <div className="offer-preview-page__signature-column">
                <div className="offer-preview-page__signature-place-date offer-preview-page__signature-place-date--empty">
                  <span className="offer-preview-page__signature-line"></span>
                </div>
                <div className="offer-preview-page__signature-place-date-label">
                  {getOfferText(currentLanguage, 'signaturePlaceDate')}
                </div>

                <div className="offer-preview-page__signature-line-wrapper">
                  <div className="offer-preview-page__signature-line"></div>
                </div>
                <div className="offer-preview-page__signature-meta">
                  <div className="offer-preview-page__signature-role-label">
                    {getOfferText(currentLanguage, 'signatureCustomerLabel')}
                    {providerCompany && customerSignerName ? '' : ''}
                  </div>
                  {customerSignerName && (
                    <div className="offer-preview-page__signature-name">{customerSignerName}</div>
                  )}
                  {customerSignerRole && (
                    <div className="offer-preview-page__signature-role">{customerSignerRole}</div>
                  )}
                </div>
              </div>

              {/* Provider column (us) */}
              <div className="offer-preview-page__signature-column">
                <div className="offer-preview-page__signature-place-date">
                  {placeDate
                    ? `${placeDate}, ${formatDate(Date.now())}`
                    : formatDate(Date.now())
                  }
                </div>
                <div className="offer-preview-page__signature-place-date-label">
                  {placeDate
                    ? getOfferText(currentLanguage, 'signaturePlaceDate')
                    : (currentLanguage === 'de' ? 'Datum' : 'Date')
                  }
                </div>

                <div className="offer-preview-page__signature-line-wrapper">
                  {providerSignature && (
                    <img
                      src={providerSignature}
                      alt={`${providerName}'s signature`}
                      className="offer-preview-page__signature-image"
                      style={{ maxWidth: `${providerSignatureMaxWidth}px` }}
                    />
                  )}
                  <div className="offer-preview-page__signature-line"></div>
                </div>
                <div className="offer-preview-page__signature-meta">
                  <div className="offer-preview-page__signature-role-label">
                    {getOfferText(currentLanguage, 'signatureProviderLabel')}
                    {providerCompany ? ` ${providerCompany}` : ''}
                  </div>
                  {providerName && (
                    <div className="offer-preview-page__signature-name">{providerName}</div>
                  )}
                  {providerRole && (
                    <div className="offer-preview-page__signature-role">{providerRole}</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {!offer.showSignatureSection && (
      <div className="offer-preview-page__footer">
        <div className="offer-preview-page__company-info">
          <p>{getOfferText(currentLanguage, 'offerCreatedBy')}</p>
          <p className="offer-preview-page__company-name">
            {offerData.teamMember ? `${offerData.teamMember.name} - ${offerData.teamMember.companyName}` : companySettings.defaultCompanyName}
          </p>
          {offerData.teamMember?.signature && (
            <div className="offer-preview-page__signature">
              <img 
                src={offerData.teamMember.signature} 
                alt={`${offerData.teamMember.name}'s signature`}
                style={{ maxWidth: `${offerData.teamMember.signatureMaxWidth || 150}px` }}
              />
            </div>
          )}
        </div>
        <div className="offer-preview-page__contact">
          <p>{getOfferText(currentLanguage, 'questionsContact')}</p>
          <p>
            {offerData.teamMember ? (
              `${offerData.teamMember.personalEmail} | ${offerData.teamMember.phone}`
            ) : (
              getOfferText(currentLanguage, 'defaultContact')
            )}
          </p>
        </div>
      </div>
      )}

      <div className="offer-preview-page__company-footer">
        <div className="offer-preview-page__company-footer-left">
          <p className="offer-preview-page__company-footer-title">
            {offerData.teamMember ? offerData.teamMember.companyName : companySettings.defaultCompanyName}
          </p>
          <p>{offerData.teamMember ? offerData.teamMember.companySlogan : getOfferText(currentLanguage, 'defaultCompanySlogan')}</p>
          
          {offerData.teamMember && (offerData.teamMember.ceo || offerData.teamMember.tradeRegister) && (
            <p style={{ marginTop: '10px', fontSize: '0.8rem' }}>
              {offerData.teamMember.ceo && (
                <span>{currentLanguage === 'de' ? 'Geschäftsführer' : 'CEO'}: {offerData.teamMember.ceo}<br /></span>
              )}
              {offerData.teamMember.tradeRegister && (
                <span>{currentLanguage === 'de' ? 'Handelsregister' : 'Trade Register'}: {offerData.teamMember.tradeRegister}</span>
              )}
            </p>
          )}
        </div>
        {offerData.teamMember && offerData.teamMember.bankName && (
          <div className="offer-preview-page__company-footer-center">
            <p className="offer-preview-page__company-footer-title">{getOfferText(currentLanguage, 'bankingDetails')}</p>
            <p>IBAN: {offerData.teamMember.iban}</p>
            <p>BIC: {offerData.teamMember.bic}</p>
          </div>
        )}
        <div className="offer-preview-page__company-footer-right">
          <p className="offer-preview-page__company-footer-title">{getOfferText(currentLanguage, 'contactHeader')}</p>
          <p>{offerData.teamMember ? offerData.teamMember.website : getOfferText(currentLanguage, 'defaultWebsite')}</p>
          <p>{offerData.teamMember ? offerData.teamMember.generalEmail : getOfferText(currentLanguage, 'defaultEmail')}</p>
        </div>
      </div>
    </div>
  );
};

export default OfferPreviewPage; 